import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router';
import { InfoCircleOutlined } from '@ant-design/icons';
import { CircularProgress } from '@material-ui/core';
import {
  getTemplates,
  updateDraft as updateDraftService,
  saveNewDraftService,
} from '../../../../redux/services';
import { selectedOrganizationSelector } from '../../../../redux/selectors/organization';
import { userDetailSelector } from '../../../../redux/selectors/user';
import {
  fullloader,
  toast,
  resetAudienceBuilder,
} from '../../../../redux/action';
import queryString from 'query-string';
import {
  Container,
  MessageContainer,
  Text,
  FullLengthContainer,
} from '../_PageUI/Page.styled';
import TemplateCard from '../TemplateCard';
import useCountCardsPerRow from '../_utils/useCountCardsPerRow';
import { ROWS_TO_LOAD } from '../_utils/constants';

const SharedTemplates = ({
  cardsBySearch,
  loadingCardsBySearch,
  searchValue,
  loadSearchCardsRef,
  hasMoreSearchCardsToLoad,
  setCardsBySearch,
}) => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const observer = useRef();
  const { draft: draftId, apply } = queryString.parse(location.search);
  const applyingToDraft = apply === '1';
  const selectedOrganization = useSelector(selectedOrganizationSelector);
  const userDetail = useSelector(userDetailSelector);
  const [hasMoreTemplatesToLoad, setHasMoreTemplatesToLoad] = useState(false);
  const [loadingMoreSavedTemplates, setLoadingMoreSavedTemplates] =
    useState(false);
  const [sharedTemplates, setSharedTemplates] = useState([]);
  const cardsPerRow = useCountCardsPerRow();
  const hasSomeSavedTemplatesRendered = searchValue
    ? cardsBySearch?.length > 0
    : sharedTemplates?.length > 0;

  const loadSharedTemplates = useCallback(async () => {
    try {
      // Don't load more if we're already loading
      if (loadingMoreSavedTemplates) {
        return;
      }

      setLoadingMoreSavedTemplates(true);
      const skip = sharedTemplates?.length || 0;
      let limit = cardsPerRow * ROWS_TO_LOAD;

      const { result, left } = await getTemplates({
        skip,
        limit,
        searchValue,
        orgType: selectedOrganization.orgType,
        magicCOEEnabled: selectedOrganization.magicCOEEnabled,
      });

      setSharedTemplates(prevUserTemplates => [
        ...prevUserTemplates,
        ...result,
      ]);
      setHasMoreTemplatesToLoad(left > 0);
      setLoadingMoreSavedTemplates(false);
    } catch (e) {
      console.error(e);
      setLoadingMoreSavedTemplates(false);
      dispatch(toast('error', 'Error loading shared templates.'));
    }
  }, [
    dispatch,
    cardsPerRow,
    searchValue,
    loadingMoreSavedTemplates,
    hasMoreTemplatesToLoad,
    sharedTemplates.length,
  ]);

  // Initial load effect
  useEffect(() => {
    dispatch(fullloader(true, 'Loading Shared Templates...'));
    loadSharedTemplates().finally(() => {
      dispatch(fullloader(false));
    });
  }, [dispatch, selectedOrganization.id, sharedTemplates.length]);

  const applySharedTemplateToDraft = async template => {
    try {
      dispatch(fullloader(true, 'Applying selected template...'));
      let draftIdentifier = draftId;

      if (draftId === 'new') {
        const draft = await saveNewDraftService({
          html: template.html,
          design: template.design,
          organizationId: selectedOrganization.id,
          userId: userDetail.id,
        });

        draftIdentifier = draft._id;
      } else {
        await updateDraftService({
          _id: draftId,
          html: template.html,
          design: template.design,
        });
      }

      dispatch(fullloader(false));
      history.push(`/dashboard/sendmessage?draft=${draftIdentifier}`);
    } catch (e) {
      dispatch(
        toast('error', `Error applying template to draft. ${e.message}`)
      );
    }
  };

  const openNoteRouterTemplate = async template => {
    dispatch(fullloader(true, 'Applying selected template...'));
    const draft = await saveNewDraftService({
      design: template.design,
      html: template.html,
      organizationId: selectedOrganization.id,
      userId: userDetail.id,
    });
    dispatch(resetAudienceBuilder());
    dispatch(fullloader(false));
    history.push(`/dashboard/sendmessage?draft=${draft._id}`);
  };

  const sharedTemplatesText =
    "These are templates created by outside designers, such as from NoteRouter. The originals can't be edited or deleted, but you can use them to create your own derivative works however you'd like.";

  const getSearchResults = () => {
    if (loadingCardsBySearch && !cardsBySearch.length) {
      return (
        <FullLengthContainer>
          <CircularProgress size={50} />
        </FullLengthContainer>
      );
    }

    if (cardsBySearch?.length) {
      return (
        <>
          {cardsBySearch.map(template => (
            <TemplateCard
              key={template._id}
              template={template}
              onClick={
                applyingToDraft
                  ? () => applySharedTemplateToDraft(template)
                  : () => openNoteRouterTemplate(template)
              }
              setCardsBySearch={setCardsBySearch}
            />
          ))}
          {hasMoreSearchCardsToLoad ? (
            loadingCardsBySearch ? (
              <FullLengthContainer>
                <CircularProgress size={50} />
              </FullLengthContainer>
            ) : (
              <FullLengthContainer ref={lastElemComponentRef} />
            )
          ) : null}
        </>
      );
    }

    return <>No Templates found.</>;
  };

  const lastElemComponentRef = lastElement => {
    if (hasMoreTemplatesToLoad) {
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          if (searchValue) {
            loadSearchCardsRef.current(searchValue);
          } else {
            loadSharedTemplates();
          }
        }
      });

      if (lastElement) observer.current.observe(lastElement);
    }
  };

  return (
    <>
      <MessageContainer>
        <InfoCircleOutlined />
        <Text>{sharedTemplatesText}</Text>
      </MessageContainer>
      <Container>
        {cardsBySearch?.length || loadingCardsBySearch || searchValue ? (
          getSearchResults()
        ) : (
          <>
            {sharedTemplates.map(template => (
              <TemplateCard
                key={template._id}
                template={template}
                onClick={
                  applyingToDraft
                    ? () => applySharedTemplateToDraft(template)
                    : () => openNoteRouterTemplate(template)
                }
                setSharedTemplates={setSharedTemplates}
              />
            ))}
            {hasSomeSavedTemplatesRendered ? (
              loadingMoreSavedTemplates ? (
                <FullLengthContainer>
                  <CircularProgress size={50} />
                </FullLengthContainer>
              ) : (
                <FullLengthContainer ref={lastElemComponentRef} />
              )
            ) : null}
          </>
        )}
      </Container>
    </>
  );
};

export default SharedTemplates;
